/**
 * The search with wildcards every app shares (BLP/nx#3473), the rule Formica's
 * search fields had: a typed query that carries a `%` is matched as typed —
 * `%` stands for any run of characters, `_` for exactly one, `\` makes the next
 * character literal — so `1%` finds what starts with 1, `%1` what ends with 1
 * and `%1%` what contains 1. A query without a `%` is a contains search, and
 * its text is literal.
 *
 * One rule in five forms: `hasWildcard` for a caller that picks its own
 * operator, `wildcardCondition` for the list filter DSL (`pattern` /
 * `contains`), `wildcardLike` for a LIKE pattern, `wildcardPattern` for a
 * wildcard query bound as typed, and `matchWildcard` for a list filtered in the
 * browser. Case is ignored by simple Unicode case folding (the `iu` regular
 * expression), the folding the Go twin's regexp uses too, so both agree letter
 * for letter. The backend twin is `backend/module/search/wildcard`; both run
 * its `testdata/vectors.json`. Pure functions: importing this costs no UI.
 */

/** Whether a typed query is a search with wildcards: it carries a `%`. */
export function hasWildcard(query: string): boolean {
  return query.includes('%');
}

/** Escapes the LIKE wildcards `%` and `_` and the escape character `\`. */
export function escapeLike(text: string): string {
  return text.replace(/[\\%_]/g, (c) => `\\${c}`);
}

/**
 * A wildcard query as the LIKE pattern to bind: as typed, with a trailing lone
 * `\` made literal — Postgres refuses a pattern that ends in its escape
 * character, and `matchWildcard` reads such a backslash as itself.
 */
export function wildcardPattern(query: string): string {
  const trailing = query.length - query.replace(/\\+$/, '').length;
  return trailing % 2 === 1 ? `${query}\\` : query;
}

/**
 * The case-insensitive LIKE pattern of a typed query (Postgres' default escape
 * character): the query as typed (`wildcardPattern`) when it has a wildcard,
 * otherwise the query anywhere with `\`, `%` and `_` escaped.
 */
export function wildcardLike(query: string): string {
  return hasWildcard(query) ? wildcardPattern(query) : `%${escapeLike(query)}%`;
}

/** One condition of the list filter DSL: `filter[<field>][<op>]=<value>`. */
export type WildcardCondition = { op: 'pattern' | 'contains'; value: string };

/**
 * The condition of the list filter DSL for a typed text query: `pattern` with
 * the query as typed when it has a wildcard, otherwise `contains` — the server
 * matches the text anywhere, literally (it escapes `%`, `_` and `\` itself).
 */
export function wildcardCondition(query: string): WildcardCondition {
  return hasWildcard(query)
    ? { op: 'pattern', value: wildcardPattern(query) }
    : { op: 'contains', value: query };
}

/**
 * Whether `text` matches a typed query under the same rule, ignoring case: as
 * typed with a wildcard (the whole text), otherwise the query anywhere in it.
 * An empty query matches every text.
 */
export function matchWildcard(text: string, query: string): boolean {
  return compile(query).test(text);
}

const compiled = new Map<string, RegExp>();

/**
 * A typed query as a case-insensitive regular expression: a plain query as a
 * literal found anywhere, a wildcard query anchored to the whole text.
 */
function compile(query: string): RegExp {
  const known = compiled.get(query);
  if (known) return known;
  let source: string;
  if (!hasWildcard(query)) {
    source = Array.from(query, quote).join('');
  } else {
    const chars = Array.from(query);
    source = '^';
    for (let i = 0; i < chars.length; i++) {
      const c = chars[i];
      if (c === '%') source += '[\\s\\S]*';
      else if (c === '_') source += '[\\s\\S]';
      else if (c === '\\') source += quote(i + 1 < chars.length ? chars[++i] : '\\');
      else source += quote(c);
    }
    source += '$';
  }
  const re = new RegExp(source, 'iu');
  if (compiled.size > 200) compiled.clear();
  compiled.set(query, re);
  return re;
}

/**
 * One character as a literal of a `u` regular expression. Only the syntax
 * characters may be escaped there — `\-` is a syntax error under `u`.
 */
function quote(c: string): string {
  return /[\^$\\.*+?()[\]{}|/]/.test(c) ? `\\${c}` : c;
}
